import { TrackReferenceOrPlaceholder, useTracks } from '@livekit/components-react'
import { Participant, Room, Track } from 'livekit-client'
import { useMemo } from 'react'

export type MuseTracks = {
    all: TrackReferenceOrPlaceholder[]
    host: TrackReferenceOrPlaceholder | undefined
    screen: TrackReferenceOrPlaceholder | undefined
}

export default function useMuseTracks(room: Room, hostUserId?: string): MuseTracks {
    const rawTracks = useTracks([Track.Source.Camera, Track.Source.ScreenShare], {
        onlySubscribed: true,
    })

    const [all, host, screen] = useMemo(() => {
        const filteredTracks = rawTracks.filter(
            (tr) =>
                tr.publication.kind === Track.Kind.Video &&
                tr.participant.identity !== room.localParticipant.identity &&
                !isListenOnlyClient(tr.participant)
        )
        let tracks: TrackReferenceOrPlaceholder[] = [...filteredTracks]
        for (const [_participantId, participant] of room.remoteParticipants) {
            const participantHasCamera = !!filteredTracks.find((t) => t.participant.sid === participant.sid)
            if (!participantHasCamera && !participant.isAgent && !isListenOnlyClient(participant)) {
                tracks.push({ participant, source: Track.Source.Camera })
            }
        }

        const hostTrack = tracks.find((r) => r.participant.metadata === hostUserId && r.source === Track.Source.Camera)
        const screenTrack = tracks.find((t) => t.source === Track.Source.ScreenShare)

        return [tracks, hostTrack, screenTrack]
    }, [rawTracks, room.remoteParticipants])

    return {
        all: all,
        host: host,
        screen: screen,
    }
}

function isListenOnlyClient(participant: Participant): boolean {
    let listenOnlyClient = false
    try {
        const metadata = JSON.parse(participant.metadata ?? '')
        listenOnlyClient = metadata.manifest.clientType === 'listenonly'
    } catch (err) {
        console.error('Failed to parse participant metadata', err)
        // NOTE: We need to return true, because clients initially have no metadata,
        // so they need to be hidden until it's confirmed they are not a listening client.
        return true
    }

    return listenOnlyClient
}
